import React from 'react';
import { Check, X, Users, Briefcase, Award } from 'lucide-react';
import { comparisonData } from '../data/mock';

const Comparison = () => {
  const iconMap = {
    "Hire In-House Team": Users,
    "Hire Freelancers": Briefcase,
    "OwnStack": Award
  };

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white" id="comparison">
      <div className="container mx-auto px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0E3A5F] mb-4">
              {comparisonData.title}
            </h2>
            <p className="text-xl text-gray-600">
              {comparisonData.subtitle}
            </p>
          </div>

          {/* Comparison Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {comparisonData.options.map((option, index) => {
              const Icon = iconMap[option.title];
              const isBizsoft = option.title === "OwnStack";
              
              return (
                <div
                  key={index}
                  className={`relative bg-white rounded-2xl shadow-lg border-2 overflow-hidden transition-all duration-300 hover:shadow-2xl ${
                    isBizsoft 
                      ? 'border-[#7AC943] transform md:scale-105' 
                      : 'border-gray-200 hover:border-[#1F6FB2]'
                  }`}
                >
                  {/* Recommended Badge */}
                  {isBizsoft && (
                    <div className="absolute top-0 right-0 bg-[#7AC943] text-white px-4 py-1 text-sm font-bold rounded-bl-lg">
                      RECOMMENDED
                    </div>
                  )}

                  {/* Header */}
                  <div className={`p-6 ${isBizsoft ? 'bg-gradient-to-br from-[#7AC943]/10 to-[#1F6FB2]/10' : 'bg-gray-50'}`}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        isBizsoft ? 'bg-[#7AC943]' : 'bg-gray-300'
                      }`}>
                        <Icon className={`h-6 w-6 ${isBizsoft ? 'text-white' : 'text-gray-600'}`} />
                      </div>
                      <h3 className="text-xl font-bold text-[#0E3A5F]">
                        {option.title}
                      </h3>
                    </div>

                    {/* Quick Stats */}
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="bg-white rounded-lg p-2">
                        <div className="text-xs text-gray-600">Timeline</div>
                        <div className={`text-sm font-bold ${isBizsoft ? 'text-[#7AC943]' : 'text-gray-800'}`}>
                          {option.timeline}
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-2">
                        <div className="text-xs text-gray-600">Cost</div>
                        <div className={`text-sm font-bold ${isBizsoft ? 'text-[#7AC943]' : 'text-gray-800'}`}>
                          {option.cost}
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-2">
                        <div className="text-xs text-gray-600">Risk</div>
                        <div className={`text-sm font-bold ${
                          option.risk === 'Low' ? 'text-[#7AC943]' : 
                          option.risk === 'High' ? 'text-orange-600' : 'text-red-600'
                        }`}>
                          {option.risk}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="p-6">
                    {/* Pros */}
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-[#7AC943] mb-3 flex items-center gap-2">
                        <Check className="h-4 w-4" />
                        Advantages
                      </h4>
                      <ul className="space-y-2">
                        {option.pros.map((pro, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                            <Check className="h-4 w-4 text-[#7AC943] flex-shrink-0 mt-0.5" />
                            <span>{pro}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Cons */}
                    <div>
                      <h4 className="text-sm font-semibold text-red-600 mb-3 flex items-center gap-2">
                        <X className="h-4 w-4" />
                        Challenges
                      </h4>
                      <ul className="space-y-2">
                        {option.cons.map((con, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                            <X className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                            <span>{con}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Conclusion */}
          <div className="bg-gradient-to-br from-[#1F6FB2]/10 to-[#7AC943]/10 p-8 rounded-2xl border-2 border-[#7AC943]/30 text-center mb-8">
            <p className="text-lg text-gray-700 leading-relaxed max-w-3xl mx-auto">
              {comparisonData.conclusion}
            </p>
          </div>

          {/* AI Advantage Section */}
          <div className="bg-gradient-to-br from-[#0E3A5F] to-[#1F6FB2] p-8 md:p-10 rounded-2xl shadow-2xl text-white">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-start gap-4 mb-6">
                <div className="flex-shrink-0 w-12 h-12 bg-[#7AC943] rounded-full flex items-center justify-center">
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl md:text-3xl font-bold mb-3">
                    {comparisonData.aiAdvantage.title}
                  </h3>
                  <p className="text-white/90 text-lg mb-6">
                    {comparisonData.aiAdvantage.description}
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-6">
                {comparisonData.aiAdvantage.benefits.map((benefit, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-3 bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/20"
                  >
                    <Check className="h-5 w-5 text-[#7AC943] flex-shrink-0 mt-0.5" />
                    <span className="text-white/95">{benefit}</span>
                  </div>
                ))}
              </div>

              <div className="bg-white/10 backdrop-blur-sm p-5 rounded-xl border border-[#7AC943]/50">
                <p className="text-white/90 text-sm leading-relaxed italic">
                  💡 {comparisonData.aiAdvantage.note}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Comparison;
